import { useCallback, useEffect, useRef, useState } from 'react';

import { CreateGenerativeTaskInput, GenerativeTaskStatusEnum } from '@/types/schema';

import { GenerativeTaskFragment } from '../graphql/fragments/generated/GenerativeTaskFragment';
import { useCancelGenerativeTaskMutation } from '../graphql/mutations/generated/cancelGenerativeTask';
import { useCreateGenerativeTaskMutation } from '../graphql/mutations/generated/createGenerativeTask';
import { useGenerativeTaskQuery } from '../graphql/queries/generated/generativeTaskQuery';
import { useGenerativeTaskSubscription } from '../graphql/subscriptions/generated/generativeTaskSubscription';
import useAIError from './useAIError';

export default function useGenerativeTaskContextValue() {
  const [generativeTaskId, setGenerativeTaskId] = useState<string | null>(null);
  const [generativeTask, setGenerativeTask] = useState<GenerativeTaskFragment | null>(null);
  const [{ fetching: isCreating }, executeCreateGenerativeTask] = useCreateGenerativeTaskMutation();
  const [{ fetching: isCancelling }, executeCancelGenerativeTask] =
    useCancelGenerativeTaskMutation();
  const { handleAIError } = useAIError();

  const errorHandlerRef = useRef<() => void>();

  const isCancelled =
    generativeTaskId && generativeTask
      ? generativeTask.status === GenerativeTaskStatusEnum.Cancelled
      : false;

  const isFailed =
    generativeTaskId && generativeTask
      ? generativeTask.status === GenerativeTaskStatusEnum.Failed
      : false;

  const isCompleted =
    generativeTaskId && generativeTask
      ? generativeTask.status === GenerativeTaskStatusEnum.Completed
      : false;

  const isDone = isCancelled || isFailed || isCompleted;

  const isTyping = generativeTaskId && generativeTask ? !isDone : false;

  const isCancellable = generativeTaskId ? !isDone : false;

  const createGenerativeTask = useCallback(
    async (input: CreateGenerativeTaskInput) => {
      const { data, error } = await executeCreateGenerativeTask({
        input,
      });

      if (error) {
        errorHandlerRef.current?.();
        return;
      }

      if (!data?.createGenerativeTask?.generativeTask) {
        errorHandlerRef.current?.();
        return;
      }

      if (data.createGenerativeTask.generativeTask.error) {
        handleAIError(data.createGenerativeTask.generativeTask.error);
        errorHandlerRef.current?.();
      }

      const generativeTaskId = data.createGenerativeTask.generativeTask.id;
      setGenerativeTaskId(generativeTaskId);
    },
    [executeCreateGenerativeTask, handleAIError],
  );

  const cancelGenerativeTask = useCallback(async () => {
    if (!generativeTaskId) {
      return;
    }

    await executeCancelGenerativeTask({
      input: {
        generativeTaskId: generativeTaskId,
      },
    });
  }, [executeCancelGenerativeTask, generativeTaskId]);

  const clearGenerativeTask = useCallback(() => {
    setGenerativeTaskId(null);
    // NOTE: some query / subscription may still be running
    // so task value may get restored later
    // add check for generativeTaskId to avoid this
    // generativeTaskId ? generativeTask : null,
    setGenerativeTask(null);
  }, []);

  const [{ data: generativeTaskSubscriptionData, fetching: isSubscribing }] =
    useGenerativeTaskSubscription({
      variables: {
        data: {
          generativeTaskId: generativeTaskId ?? '',
        },
      },
      pause: !generativeTaskId,
    });

  const isWaitingForFirstResponse =
    generativeTaskId && generativeTask ? !isSubscribing && !isTyping && !isDone : false;

  const isLoading = isCreating || isSubscribing || isWaitingForFirstResponse;

  const [{ data: generativeTaskQueryData }] = useGenerativeTaskQuery({
    variables: {
      filters: {
        generativeTaskId: generativeTaskId ?? '',
      },
    },
    pause: !generativeTaskId,
    requestPolicy: 'network-only',
  });

  useEffect(() => {
    if (
      generativeTaskSubscriptionData &&
      generativeTaskSubscriptionData.generativeTask.generativeTaskId === generativeTaskId &&
      generativeTaskSubscriptionData.generativeTask.generativeTask
    ) {
      if (generativeTaskSubscriptionData.generativeTask.generativeTask.error) {
        handleAIError(generativeTaskSubscriptionData.generativeTask.generativeTask.error);
        errorHandlerRef.current?.();
      }

      setGenerativeTask(generativeTaskSubscriptionData.generativeTask.generativeTask ?? null);
    }

    if (generativeTaskQueryData && generativeTaskQueryData.generativeTask.generativeTask) {
      if (generativeTaskQueryData.generativeTask.generativeTask.error) {
        handleAIError(generativeTaskQueryData.generativeTask.generativeTask.error);
        errorHandlerRef.current?.();
      }

      setGenerativeTask(generativeTaskQueryData.generativeTask.generativeTask);
    }
  }, [generativeTaskId, generativeTaskQueryData, generativeTaskSubscriptionData, handleAIError]);

  return {
    // state
    generativeTaskId,
    generativeTask: generativeTaskId ? generativeTask : null,
    // flags
    isCreating,
    isSubscribing,
    isWaitingForFirstResponse,
    isLoading,
    isTyping,
    isCancellable,
    isCancelling,
    isCancelled,
    isFailed,
    isCompleted,
    isDone,
    // methods
    createGenerativeTask,
    cancelGenerativeTask,
    clearGenerativeTask,
    // event handlers
    errorHandlerRef,
  };
}
